<template>
  <div class="ai-generate-modal-wrapper">
    <Modal
      :value="value"
      @input="(v) => $emit('input', v)"
      :title="modalTitle"
      width="856px"
      :mask-closable="false"
      :closable="!generating"
      class-name="ai-generate-modal"
    >
      <!-- 生图提示词 -->
      <div class="ai-gen-section">
        <div class="ai-gen-section-title">
          <span class="required-star">*</span> 生图提示词 (Prompt)
        </div>
        <Input
          v-model="prompt"
          type="textarea"
          :rows="4"
          :maxlength="500"
          placeholder="例如：将台灯放在现代简约风格的床头柜上，柔和暖光，大理石背景，超高清..."
          :disabled="generating"
        />
      </div>

      <!-- 参考图片 -->
      <div class="ai-gen-section">
        <div class="ai-gen-section-title">参考图片</div>
        <div class="ai-gen-ref-box">
          <!-- 已选参考图缩略图 -->
          <div
            v-for="(img, idx) in referenceImages"
            :key="`ref-${idx}-${img.path}`"
            class="ai-gen-ref-item"
            :title="getRefImageTooltip(img)"
          >
            <img :src="filenodeViewTargetUrl + img.path" />
            <!-- 来源徒标（本地/SPU语言/SKU语言＋图套） -->
            <div class="ai-gen-ref-badges">
              <span
                v-if="img.sourceType === 'upload'"
                class="ai-gen-ref-badge is-upload"
                >本地</span
              >
              <template v-else>
                <span class="ai-gen-ref-badge is-lang">{{
                  getLangLabel(img.language)
                }}</span>
                <span
                  v-if="dimension === 'sku' && img.setName"
                  class="ai-gen-ref-badge is-set"
                  :style="{ background: getSetTypeBadgeColor(img.setType) }"
                  >{{ img.setName }}</span
                >
              </template>
            </div>
            <!-- hover 遮罩 + 删除按钮 -->
            <div class="ai-gen-ref-cover">
              <Icon
                type="ios-trash-outline"
                title="移除"
                @click.native.stop="removeReferenceImage(idx)"
              />
            </div>
          </div>
          <!-- 上传本地 -->
          <dytUpload
            v-if="!generating"
            file-name="files"
            ref="uploadRef"
            :headers="uploadFilesHeader"
            :max-size="uploadFilesMaxSize"
            :on-exceeded-size="handleExceedSize"
            :show-upload-list="false"
            :format="['jpg', 'png', 'gif']"
            accept="image/*"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-format-error="handleFormatError"
            multiple
            :before-upload="beforeUpload"
            :action="uploadFilesUrl"
            class="ai-gen-ref-upload"
          >
            <div class="ai-gen-ref-add-item">
              <Icon type="ios-folder-outline" size="38" color="#bbb" />
              <div class="ai-gen-ref-add-text">上传本地</div>
            </div>
          </dytUpload>
          <!-- 选用已有图案 -->
          <div
            class="ai-gen-ref-add-item"
            @click="openSelectImageModal"
            v-if="!generating"
          >
            <Icon type="ios-images-outline" size="38" color="#bbb" />
            <div class="ai-gen-ref-add-text">选用已有图案</div>
          </div>
        </div>
      </div>

      <!-- 保存目标配置 -->
      <div class="ai-gen-section">
        <div class="ai-gen-section-title">
          保存目标配置
          <span class="ai-gen-hint">
            （生成后直接存入对应{{ dimension === "sku" ? "图套" : "语言" }}）
          </span>
        </div>
        <div class="ai-gen-target-box">
          <div class="ai-gen-target-row">
            <div class="ai-gen-target-col">
              <label class="ai-gen-target-label">
                <span class="required-star">*</span>选择存储语言
              </label>
              <Select
                v-model="targetLanguage"
                :disabled="generating"
                transfer
                @on-change="onLanguageChange"
              >
                <Option
                  v-for="lang in langOptions"
                  :key="lang.value"
                  :value="lang.value"
                  >{{ lang.label }}</Option
                >
              </Select>
            </div>
            <div class="ai-gen-target-col" v-if="dimension === 'sku'">
              <label class="ai-gen-target-label">
                <span class="required-star">*</span>选择存储图套
              </label>
              <!--  || currentSetOptions.length === 0 -->
              <Select
                v-model="targetImageSetId"
                :disabled="generating"
                transfer
                :placeholder="
                  currentSetOptions.length === 0
                    ? '当前语言下暂无图套'
                    : '请选择图套'
                "
              >
                <Option
                  v-for="s in currentSetOptions"
                  :key="s.productGoodsImageSetId"
                  :value="s.productGoodsImageSetId"
                  >{{ s.setName }} ({{ getSetTypeLabel(s.setType) }})</Option
                >
                <Option :value="'__add_new__'" v-if="!generating">+ 添加图套</Option>
              </Select>
            </div>
          </div>
          <!-- 同步选项（仅 SKU） -->
          <div v-if="dimension === 'sku'" class="ai-gen-sync-row">
            <Checkbox v-model="syncSameModel" :disabled="generating"
              >同步存储至相同 model 的 SKU</Checkbox
            >
            <Checkbox
              v-model="syncSameColor"
              :disabled="generating"
              style="margin-left: 16px"
              >同步存储至相同 color 的 SKU</Checkbox
            >
          </div>
        </div>
      </div>

      <!-- 底部按钮 -->
      <div slot="footer">
        <Button @click="handleCancel" :disabled="generating">取 消</Button>
        <Button type="primary" @click="handleGenerate" :loading="generating">
          {{ generating ? "生成中..." : "开始生成" }}
        </Button>
      </div>
    </Modal>

    <!-- 二级选图弹窗 -->
    <aiSelectImageModal
      v-model="selectImageModalVisible"
      :dimension="dimension"
      :multi-lang-image-data="multiLangImageData"
      :sku-image-set-list="skuImageSetList"
      :pre-selected="existingImagesPreSelected"
      :filenode-view-target-url="filenodeViewTargetUrl"
      @on-ok="handleSelectImageOk"
    />
    <!-- 添加图套弹窗（公共表单组件） -->
    <imageSetFormModal ref="imageSetFormModal" @on-confirm="handleImageSetFormConfirm" />
  </div>
</template>

<script>
import api from "@/api/api";
import aiSelectImageModal from "./aiSelectImageModal";
import imageSetFormModal from "./imageSetFormModal";

/**
 * @description AI生图 - 主弹窗（SPU/SKU 共用）
 * @author 前端开发
 * @date 2026-07-21
 * @功能说明
 *   1. Prompt 提示词必填校验
 *   2. 参考图片选填：本地上传（走 api.upload_files）+ 选用已有图案（打开二级弹窗）
 *   3. 保存目标配置：SPU 单个下拉选语言；SKU 级联下拉选语言+图套
 *   4. SKU 独有同步选项：两个 checkbox 可同时勾选，映射为 applySpecificationNames: ["Size","Color"]
 *   5. "开始生成"目前是占位方法，控制台打印完整 payload 结构，模拟 loading 后关闭
 * @参考图片数据结构
 *   本地上传:  { path, sourceType: 'upload' }
 *   SPU已有:   { path, imageId, language, setName }                             (imageId 取自后端 productImageId)
 *   SKU已有:   { path, imageId, productGoodsImageSetId, language, setName, setType }  (imageId 取自后端 productGoodsImageId)
 */
export default {
  name: "aiGenerateModal",
  components: {
    aiSelectImageModal,
    imageSetFormModal,
  },
  props: {
    /** 弹窗显示（配合 v-model 使用） */
    value: {
      type: Boolean,
      default: false,
    },
    /** 维度：'spu' 或 'sku' */
    dimension: {
      type: String,
      default: "spu",
      validator: (v) => ["spu", "sku"].includes(v),
    },
    /** 商品ID，保存图套接口需要 */
    productId: {
      type: String,
      default: "",
    },
    /** SKU 的 productGoodsId（SKU 维度必传） */
    productGoodsId: {
      type: [String, Number],
      default: "",
    },
    /** SPU 多语言图片数据（供二级选图弹窗展示 & 本地上传后写入使用） */
    multiLangImageData: {
      type: Array,
      default: () => [],
    },
    /** SKU 图套列表（SKU 维度使用） */
    skuImageSetList: {
      type: Array,
      default: () => [],
    },
    /** 文件服务器查看地址前缀 */
    filenodeViewTargetUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      /** Prompt 提示词 */
      prompt: "",
      /** 参考图片列表 */
      referenceImages: [],
      /** 保存目标语言 */
      targetLanguage: "common",
      /** 保存目标图套（SKU） */
      targetImageSetId: "",
      /** 上一次有效的图套ID（用于拦截“添加图套”后回退选中值） */
      lastValidSetId: "",
      /** 同步至相同 model（SKU） */
      syncSameModel: false,
      /** 同步至相同 color（SKU） */
      syncSameColor: false,
      /** 二级选图弹窗显示状态 */
      selectImageModalVisible: false,
      /** 生成中 loading 状态 */
      generating: false,
      /** 上传接口地址 */
      uploadFilesUrl: api.upload_files + "?basePath=/product",
      /** 上传大小限制（kb） */
      uploadFilesMaxSize: 10240,
      /** 单次最多上传数量 */
      uploadMaxLength: 20,
      /** 待上传的文件 FormData 列表 */
      uploadFilesList: [],
      /** 上传延迟定时器 */
      uploadFilesTime: null,
      /** 是否正在上传中（触发批量上传） */
      isUploading: false,
    };
  },
  computed: {
    /** 弹窗标题 */
    modalTitle() {
      return this.dimension === "spu"
        ? "AI生图 —— SPU维度"
        : "AI生图 —— SKU维度";
    },
    /** 上传请求头 */
    uploadFilesHeader() {
      return {
        ...this.$store.getters.erpRequestHeaders,
        ...this.$store.getters.dytRequestHeaders,
      };
    },
    /** 存储语言选项（与语言 tab 保持一致） */
    langOptions() {
      return [
        { value: "common", label: "通用" },
        { value: "en", label: "英语 (EN)" },
        { value: "fr", label: "法语 (FR)" },
        { value: "de", label: "德语 (DE)" },
        { value: "ru", label: "俄语 (RU)" },
        { value: "nl", label: "荷兰语 (NL)" },
      ];
    },
    /** SKU 当前语言下的图套选项 */
    currentSetOptions() {
      if (this.dimension !== "sku") return [];
      return (this.skuImageSetList || []).filter(
        (s) => s.language === this.targetLanguage,
      );
    },
    /** 参考图片中"已有图案来源"的项（供二级弹窗回显） */
    existingImagesPreSelected() {
      return this.referenceImages.filter((img) => img.sourceType !== "upload");
    },
    /** 同步选项映射到后端字段：applySpecificationNames */
    applySpecificationNames() {
      const list = [];
      if (this.syncSameModel) list.push("Size");
      if (this.syncSameColor) list.push("Color");
      return list;
    },
  },
  watch: {
    /**
     * 弹窗打开时重置表单
     * 弹窗关闭时清空 loading 状态
     */
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.resetForm();
        } else {
          this.generating = false;
        }
      },
    },
    /** 监听批量上传触发标记 */
    isUploading(val) {
      val && this.uploadFilesListHand();
    },
    /** 拦截“添加图套”选项：不真正选中，而是打开添加图套弹窗 */
    targetImageSetId(val) {
      if (val === '__add_new__') {
        this.$refs.imageSetFormModal.open('add');
        this.$nextTick(() => {
          this.targetImageSetId = this.lastValidSetId || '';
        });
      } else {
        this.lastValidSetId = val;
      }
    },
  },
  methods: {
    /** 重置表单状态 */
    resetForm() {
      this.prompt = "";
      this.referenceImages = [];
      this.targetLanguage = "common";
      // SKU：默认选中当前语言下第一个图套
      if (this.dimension === "sku") {
        const firstSet = (this.skuImageSetList || []).find(
          (s) => s.language === "common",
        );
        this.targetImageSetId = firstSet ? firstSet.productGoodsImageSetId : "";
      } else {
        this.targetImageSetId = "";
      }
      this.syncSameModel = false;
      this.syncSameColor = false;
      this.generating = false;
      this.uploadFilesList = [];
      this.isUploading = false;
    },
    /** 添加图套确认回调：去重校验 → 调接口 → 通知父组件刷新列表 */
    handleImageSetFormConfirm({ setName, setType }) {
      const language = this.targetLanguage;
      // 同一语言下图套名称去重校验
      const duplicate = (this.skuImageSetList || []).find(s => {
        return s.language === language && s.setName === setName;
      });
      if (duplicate) {
        this.$Message.warning(`图套名称已存在：${setName}`);
        this.$refs.imageSetFormModal.close();
        return;
      }
      const payload = {
        productGoodsId: this.productGoodsId,
        language,
        setName,
        setType
      };
      this.axios.post(api.add_productGoodsImageSet, payload).then(res => {
        if (res && res.data && res.data.code === 0) {
          this.$Message.success('新增图套成功');
          // 通知父组件刷新图套列表，aiGenerateModal 的 currentSetOptions 会跟着更新
          this.$emit('on-image-set-added', {
            productGoodsImageSetId: res.data.datas || String(Date.now()),
            setName,
            setType,
            language,
            productGoodsId: this.productGoodsId,
            images: [],
          });
        }
      });
      this.$refs.imageSetFormModal.close();
    },
    /** 语言变化时，SKU 维度重置图套选项 */
    onLanguageChange() {
      if (this.dimension === "sku") {
        const firstSet = this.currentSetOptions[0];
        this.targetImageSetId = firstSet ? firstSet.productGoodsImageSetId : "";
      }
    },
    /** 打开二级选图弹窗 */
    openSelectImageModal() {
      this.selectImageModalVisible = true;
    },
    /**
     * 二级选图弹窗确定回调
     * 用 imageKey 去重后合并：本地上传保留，覆盖式合并已有图案的选择结果
     */
    handleSelectImageOk(selectedList) {
      // 保留本地上传的图片
      const uploadOnes = this.referenceImages.filter(
        (img) => img.sourceType === "upload",
      );
      // 合并二级弹窗选中的"已有图案"
      this.referenceImages = [...uploadOnes, ...(selectedList || [])];
    },
    /** 移除参考图片 */
    removeReferenceImage(idx) {
      this.referenceImages.splice(idx, 1);
    },
    /**
     * 参考图片 hover 提示文本（完整来源信息）
     * @param {Object} img 参考图项
     * @returns {string}
     */
    getRefImageTooltip(img) {
      if (img.sourceType === "upload") return "来源：本地上传";
      const lang = this.getLangLabel(img.language);
      if (this.dimension === "sku" && img.setName) {
        const typeLabel = this.getSetTypeLabel(img.setType);
        return `来源：已有图案 / ${lang} / ${img.setName}${
          typeLabel ? ` (${typeLabel})` : ""
        }`;
      }
      return `来源：已有图案 / ${lang}`;
    },
    /**
     * 获取图套类型徒标颜色（与 skuImageSetModal.vue 保持一致）
     * 1=网图橙、 2=非实拍自修绿、 3=实拍自修蓝
     */
    getSetTypeBadgeColor(setType) {
      const map = { 1: "#f90", 2: "#19be6b", 3: "#2d8cf0" };
      return map[String(setType)] || "#999";
    },
    /** 语言标签显示文本 */
    getLangLabel(lang) {
      const map = {
        common: "通用",
        en: "EN",
        fr: "FR",
        de: "DE",
        ru: "RU",
        nl: "NL",
      };
      return map[lang] || lang || "";
    },
    /** 图套类型显示文本 */
    getSetTypeLabel(setType) {
      const map = { 1: "网图", 2: "非实拍自修", 3: "实拍自修" };
      return map[String(setType)] || String(setType || "");
    },
    /** 上传大小超限提示 */
    handleExceedSize() {
      this.$Message.error("文件大小超出限制，最大为10M");
    },
    /** 上传失败提示 */
    handleUploadError() {
      this.$Message.error("系统繁忙，请重新尝试");
    },
    /** 上传格式错误提示 */
    handleFormatError() {
      this.$Message.error("文件格式有误，仅支持 jpg / png / gif");
    },
    /** dytUpload 单文件成功钩子（本组件走批量上传，这里不做处理） */
    handleUploadSuccess() {},
    /**
     * 上传前钩子（拦截默认上传，改为批量手动上传，300ms防抖攒批）
     * @param {File} file
     */
    beforeUpload(file) {
      const selectedFiles = this.$refs.uploadRef.$refs.input.files;
      let curFileIndex = -1;
      for (let i = 0; i < selectedFiles.length; i++) {
        if (selectedFiles[i] === file) curFileIndex = i;
      }
      if (curFileIndex === 0 && selectedFiles.length > this.uploadMaxLength) {
        this.$Message.error(
          `文件数量超出限制，一次最多可上传${this.uploadMaxLength}张`,
        );
        return false;
      } else if (selectedFiles.length > this.uploadMaxLength) {
        return false;
      }
      const form = new FormData();
      form.append("files", file);
      this.uploadFilesTime && clearTimeout(this.uploadFilesTime);
      this.uploadFilesList.push(form);
      this.uploadFilesTime = setTimeout(() => {
        this.isUploading = true;
      }, 300);
      return false;
    },
    /** 批量上传处理 */
    uploadFilesListHand() {
      const awaitList = this.uploadFilesList.map((form) => {
        return this.axios.post(
          `${this.uploadFilesUrl}&random=${Math.random()
            .toString()
            .substring(2)}`,
          form,
        );
      });
      Promise.all(awaitList)
        .then((arr) => {
          (arr || []).forEach((res) => {
            if (res && res.data && Array.isArray(res.data.datas)) {
              res.data.datas.forEach((path) => {
                this.referenceImages.push({ path, sourceType: "upload" });
              });
            }
          });
          this.uploadFilesList = [];
          this.isUploading = false;
        })
        .catch(() => {
          this.$Message.error("上传失败，请重试");
          this.uploadFilesList = [];
          this.isUploading = false;
        });
    },
    /**
     * 生成前校验
     * @returns {boolean} 是否通过校验
     */
    validate() {
      if (!this.prompt || !this.prompt.trim()) {
        this.$Message.error("请输入生图提示词");
        return false;
      }
      if (!this.targetLanguage) {
        this.$Message.error("请选择存储语言");
        return false;
      }
      if (this.dimension === "sku") {
        if (!this.productGoodsId) {
          this.$Message.error("缺少 SKU 标识，请先保存 SKU");
          return false;
        }
        if (!this.targetImageSetId) {
          this.$Message.error("请选择存储图套");
          return false;
        }
      }
      return true;
    },
    /**
     * @returns {Object} payload 对象
     */
    buildPayload() {
      let temp = {};
      let imageIdList = []; // 图片ID列表
      let localImagePathList = []; // 本地上传路径列表
      let applyToTransfer = { Color: 2, Size: 1 }; // 图片应用到的规格
      this.referenceImages.forEach((k) => {
        if (k.imageId) {
          imageIdList.push(k.imageId);
        } else {
          localImagePathList.push(k.path);
        }
      });
      temp.prompt = (this.prompt || "").trim();
      temp.productId = this.productId;
      temp.productGoodsId = this.productGoodsId || null;
      temp.imageIdList = Array.from(new Set(imageIdList)); // 去重
      temp.localImagePathList = Array.from(new Set(localImagePathList)); // 去重
      temp.saveLanguage = this.targetLanguage;
      temp.imageSetId = this.targetImageSetId || null;
      temp.syncTo = this.applySpecificationNames
        .map((k) => applyToTransfer[k])
        .filter((k) => k);
      return temp;
    },
    /**
     * 点击"开始生成"：校验 → 组装 payload → 发请求 → 向外 emit 供父组件写入图套
     * 实际将图片写入到 SPU 图库 / SKU 图套 的逻辑由父组件在 on-generate 回调中处理（保持该弹窗无状态依赖）
     */
    handleGenerate() {
      if (this.generating) return;
      if (!this.validate()) return;
      const payload = this.buildPayload();
      // console.log(payload);
      this.generating = true;
      this.axios
        .post(api.productAiGenerateImage, payload)
        .then((res) => {
          if (res && res.data && res.data.code === 0) {
            this.$Message.success("已提交，异步处理中...");
            // 向父组件抛出 payload 和接口结果
            this.$emit("on-generate", payload, res);
            this.$emit("input", false);
          } else {
            this.$Message.error((res.data && res.data.message) || "操作失败");
          }
        })
        .catch((err) => {
          this.$Message.error(
            typeof err === "string"
              ? err
              : (err && err.message) || "生图失败，请重试",
          );
        })
        .finally(() => {
          this.generating = false;
        });
    },
    /** 点击"取消"关闭弹窗 */
    handleCancel() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped lang="less">
.ai-gen-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.ai-gen-section-title {
  color: #17233d;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;

  .required-star {
    color: #ed4014;
    margin-right: 4px;
  }

  .ai-gen-hint {
    color: #999;
    font-weight: normal;
    font-size: 12px;
    margin-left: 4px;
  }
}

.ai-gen-ref-box {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  min-height: 152px;

  .ai-gen-ref-upload {
    display: inline-block;
    vertical-align: top;
    line-height: 1;
  }

  .ai-gen-ref-item {
    position: relative;
    width: 120px;
    height: 120px;
    border: 1px solid #dcdee2;
    border-radius: 4px;
    overflow: hidden;
    background: #fff;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .ai-gen-ref-badges {
      position: absolute;
      top: 4px;
      left: 4px;
      right: 4px;
      display: flex;
      flex-wrap: wrap;
      gap: 3px;
      pointer-events: none;
    }

    .ai-gen-ref-badge {
      display: inline-block;
      max-width: 100%;
      padding: 1px 6px;
      font-size: 11px;
      line-height: 16px;
      color: #fff;
      border-radius: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);

      &.is-upload {
        background: rgba(144, 147, 153, 0.9);
      }

      &.is-lang {
        background: #19be6b;
      }

      &.is-set {
        background: #2d8cf0;
      }
    }

    .ai-gen-ref-cover {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.45);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;

      i {
        font-size: 26px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;

        &:hover {
          color: #ff6a00;
          background: rgba(255, 255, 255, 0.15);
        }
      }
    }

    &:hover .ai-gen-ref-cover {
      opacity: 1;
    }
  }

  .ai-gen-ref-add-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 120px;
    height: 120px;
    border: 1px dashed #c0c4cc;
    border-radius: 4px;
    background: #fafafa;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #2d8cf0;

      .ai-gen-ref-add-text {
        color: #2d8cf0;
      }

      i {
        color: #2d8cf0 !important;
      }
    }

    i {
      margin-bottom: 8px;
    }

    .ai-gen-ref-add-text {
      color: #666;
      font-size: 13px;
    }
  }
}

.ai-gen-target-box {
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;

  .ai-gen-target-row {
    display: flex;
    gap: 16px;

    .ai-gen-target-col {
      flex: 1;
      min-width: 0;

      .ai-gen-target-label {
        display: block;
        color: #515a6e;
        font-size: 13px;
        margin-bottom: 6px;

        .required-star {
          color: #ed4014;
          margin-right: 4px;
        }
      }
    }
  }

  .ai-gen-sync-row {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px dashed #dcdee2;
  }
}
</style>

<!-- 非 scoped 样式：处理 iView Upload 内部 DOM（使上传本地方块与选用已有图案方块同行展示） -->
<style lang="less">
.ai-generate-modal .ai-gen-ref-upload .ivu-upload {
  display: inline-block;
}
</style>
